import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import ContactSheet from "@/components/ContactSheet";
import { trackClick } from "@/lib/analytics";
import beanMustache from "@/assets/bean-mustache.svg";
import { clearConsent } from "@/lib/cookies";

const socialLinks = [
  { label: "LinkedIn", href: "https://www.linkedin.com/in/madigreen72/" },
  { label: "Instagram", href: "https://www.instagram.com/keish.studio/" },
  { label: "Pinterest", href: "https://www.pinterest.com/keishstudio/" },
  { label: "Contra", href: "https://contra.com/keishstudio" },
];

const taglines = [
  "Designed by KEISH Design Studio ⋆˚꩜｡ (duh)",
];

const FooterSection = () => {
  const [tagline, setTagline] = useState(taglines[0]);

  useEffect(() => {
    setTagline(taglines[Math.floor(Math.random() * taglines.length)]);
  }, []);

  return (
    <footer className="section-dark">
      {/* Hero CTA */}
      <div className="px-5 md:px-10 lg:px-14 pt-24 md:pt-36 pb-16 md:pb-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <ContactSheet>
            <button className="group text-left w-full">
              <h2 className="font-display italic text-[clamp(3rem,10vw,6rem)] leading-[0.9] tracking-[-0.03em] text-foreground/90 group-hover:text-foreground transition-colors duration-500">
                let's make
                <br />
                something good
                <span className="inline-block ml-3 md:ml-5 w-[0.6em] h-[2px] bg-foreground/30 group-hover:w-[1.2em] group-hover:bg-foreground/70 transition-all duration-700 align-middle" />
              </h2>
            </button>
          </ContactSheet>

          {/* Rotating bean mascot */}
          <img
            src={beanMustache}
            alt=""
            className="absolute -right-2 md:right-4 bottom-0 w-16 md:w-20 opacity-10 select-none"
            style={{ animation: "spin 25s linear infinite" }}
          />
        </motion.div>
      </div>

      {/* Links grid */}
      <div className="px-5 md:px-10 lg:px-14 py-8 md:py-10 grid grid-cols-1 sm:grid-cols-2 gap-8 sm:gap-4 border-t border-foreground/10">
        {/* Left — Navigation */}
         <div className="flex flex-col gap-3">
           <span className="font-body text-ts-2xs font-medium tracking-[0.25em] uppercase text-muted-foreground mb-1">
             Wander
           </span>
           {[
             { label: "Work", href: "#portfolio" },
             { label: "Services", href: "#services" },
             { label: "About", href: "#about" },
             { label: "Clients", href: "/clients" },
           ].map((link) => (
             <a
               key={link.label}
               href={link.href}
               className="font-body text-ts-2xs tracking-[0.08em] text-muted-foreground hover:text-foreground/80 transition-colors duration-300 w-fit"
             >
               {link.label}
             </a>
           ))}
         </div>

         {/* Right — Socials */}
         <div className="flex flex-col gap-3">
           <span className="font-body text-ts-2xs font-medium tracking-[0.25em] uppercase text-muted-foreground mb-1">
             Say hi
           </span>
           <a
             href="mailto:madi@keish.studio"
             className="font-body text-ts-2xs tracking-[0.08em] text-muted-foreground hover:text-foreground/80 transition-colors duration-300 w-fit"
           >
             madi@keish.studio
           </a>
           {socialLinks.map((link) => (
             <a
               key={link.label}
               href={link.href}
               target="_blank"
               rel="noopener noreferrer"
               onClick={() => trackClick("social", link.label.toLowerCase())}
               className="font-body text-ts-2xs tracking-[0.08em] text-muted-foreground hover:text-foreground/80 transition-colors duration-300 w-fit"
             >
               {link.label}
             </a>
           ))}
         </div>
      </div>

      {/* Credits bar */}
       <div className="px-5 md:px-10 lg:px-14 py-6 md:py-4 flex flex-col gap-4 md:gap-2 border-t border-foreground/[0.05]">
         <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-3">
           <span className="font-body text-ts-2xs text-muted-foreground/70">
             © {new Date().getFullYear()} KEISH Design Studio. All rights reserved.
           </span>
           <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
             <a href="/privacy" className="font-body text-ts-2xs text-muted-foreground/70 hover:text-muted-foreground transition-colors">Privacy</a>
             <a href="/terms" className="font-body text-ts-2xs text-muted-foreground/70 hover:text-muted-foreground transition-colors">Terms</a>
             <a href="/cookies" className="font-body text-ts-2xs text-muted-foreground/70 hover:text-muted-foreground transition-colors">Cookies</a>
             <button
               onClick={() => {
                 clearConsent();
                 window.dispatchEvent(new Event("show-cookie-banner"));
               }}
               className="font-body text-ts-2xs text-muted-foreground/70 hover:text-muted-foreground transition-colors"
             >
               Cookie Settings
             </button>
           </div>
         </div>
         <span className="font-body text-ts-2xs text-muted-foreground tracking-[0.05em]">
           {tagline}
         </span>
       </div>
    </footer>
  );
};

export default FooterSection;
